import React from "react";
import styled from "styled-components";
import { Icon } from "semantic-ui-react";

import { t } from "../../../constant/translations";
import { amountToCurrency, getAppCurrency } from "../../../helpers/currency";
import { formatDate } from "../../../helpers/date";
import {
  isServiceActive,
  relFuture,
  scheduledDelta,
  serviceStatus,
} from "../../../helpers/contract-service";

const Card = styled.div`
  border: 1px solid #e9ebee;
  border-radius: 14px;
  background: #fff;
  padding: 18px 20px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.05s;
  ${(props) =>
    props.inactive ? `background: #f7f8f9; border-style: dashed;` : ""}
  &:hover {
    border-color: #c8d4e6;
    box-shadow: 0 10px 28px rgba(20, 30, 50, 0.1);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
`;

const Id = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 10px;
  margin-top: 7px;
  background: ${(props) =>
    props.status === "active"
      ? "#33c06a"
      : props.status === "scheduled"
      ? "#e8930c"
      : "#c2c7cd"};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Name = styled.span`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: ${(props) => (props.inactive ? "#5b6068" : "#1c1917")};
`;

const Tag = styled.span`
  display: inline-block;
  margin-top: 6px;
  background: ${(props) => (props.inactive ? "#fff" : "#f7f8f9")};
  border: 1px solid #e7e5e4;
  border-radius: 6px;
  padding: 2px 9px;
  color: #5b6068;
  font-weight: 600;
  font-size: 12px;
`;

const Pill = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: ${(props) =>
    props.kind === "active"
      ? "#e8f8ee"
      : props.kind === "scheduled"
      ? "#eaf2fd"
      : "#e9ebee"};
  color: ${(props) =>
    props.kind === "active"
      ? "#1f9a52"
      : props.kind === "scheduled"
      ? "#1d6ae0"
      : "#5b6068"};
`;

const AmountWrap = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const AmountNum = styled.div`
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: ${(props) => (props.inactive ? "#5b6068" : "#1c1917")};
`;

const AmountPer = styled.div`
  font-size: 12.5px;
  color: #78716c;
  font-weight: 600;
`;

const Meta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 26px;
  margin-top: 14px;
  flex-wrap: wrap;
  & > div:first-child,
  & > div:nth-child(2) {
    flex: 0 0 auto;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const FieldLabel = styled.span`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #78716c;
  font-weight: 700;
`;

const FieldValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.muted ? "#78716c" : "#1c1917")};
`;

const SchedStrip = styled.div`
  margin-top: 14px;
  background: ${(props) => (props.ending ? "#fdecea" : "#eaf2fd")};
  border-radius: 10px;
  padding: 11px 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${(props) => (props.ending ? "#b23a29" : "#1d6ae0")};
`;

const SchedLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
`;

const SchedArrow = styled.span`
  color: #1c1917;
  font-weight: 700;
`;

const SchedRight = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
`;

const NotesField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
`;

const NotesValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #5b6068;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

export const ServiceCard = ({ service, onClick }) => {
  const status = serviceStatus(service);
  const inactive = status === "ended";
  const sc = service.scheduled_change;
  const pillKey =
    status === "ended"
      ? "status_ended"
      : status === "scheduled"
      ? "status_scheduled"
      : "status_active";

  return (
    <Card inactive={inactive} onClick={onClick}>
      <Top>
        <Id>
          <Dot status={status} />
          <div style={{ minWidth: 0 }}>
            <NameRow>
              <Name inactive={inactive}>{service.name}</Name>
              <Pill kind={status}>{t(`contract.line.${pillKey}`)}</Pill>
            </NameRow>
            {service.category && (
              <Tag inactive={inactive}>{service.category}</Tag>
            )}
          </div>
        </Id>
        <AmountWrap>
          <AmountNum inactive={inactive}>
            {amountToCurrency(
              service.amount,
              getAppCurrency(),
              getAppCurrency()
            )}
          </AmountNum>
          <AmountPer>{t("contract.per_month")}</AmountPer>
        </AmountWrap>
      </Top>

      <Meta>
        <Field>
          <FieldLabel>{t("contract.line.effective_from")}</FieldLabel>
          <FieldValue>
            {service.start_date ? formatDate(service.start_date) : "—"}
          </FieldValue>
        </Field>
        <Field>
          <FieldLabel>{t("contract.line.end_label")}</FieldLabel>
          <FieldValue muted={!service.end_date}>
            {service.end_date
              ? formatDate(service.end_date)
              : t("contract.line.no_end")}
          </FieldValue>
        </Field>
        {service.notes && (
          <NotesField>
            <FieldLabel>{t("contract.notes")}</FieldLabel>
            <NotesValue>{service.notes}</NotesValue>
          </NotesField>
        )}
      </Meta>

      {sc && (
        <SchedStrip ending={sc.type === "end"}>
          <SchedLeft>
            <Icon name="clock outline" style={{ margin: 0 }} />
            <span>
              <b>{scheduledDelta(sc)}</b>
              {sc.type !== "end" && (
                <SchedArrow>
                  {" → "}
                  {amountToCurrency(
                    sc.new_amount,
                    getAppCurrency(),
                    getAppCurrency()
                  )}
                </SchedArrow>
              )}
            </span>
          </SchedLeft>
          <SchedRight>
            {t("contract.line.preview_from_short")}{" "}
            {formatDate(sc.effective_from)} · {relFuture(sc.effective_from)}
          </SchedRight>
        </SchedStrip>
      )}
    </Card>
  );
};
